import { useAtomValue } from "jotai";
import { CSSProperties, FC } from "react";
import { indyGlassesVariantAtom } from "~/atoms/chatbot";
import "./IndyScanIndicator.css";
import { glassesById } from "./indyGlasses";

/**
 * "Indy is reading" indicator — the user's chosen glasses tracking left to
 * right with a highlight sweeping the lenses, then flicking back to the start
 * of the next line.
 *
 * This is the reading cadence that was drawn for the launcher and parked there
 * (see ChatbotLauncher.css): on the closed launcher it needed a global
 * streaming signal that does not exist, but here the caller already knows it is
 * waiting on a token, so the motion has a real trigger. Geometry comes from the
 * selected variant, since every drawing puts its lenses somewhere different.
 */
interface IndyScanIndicatorProps {
  /**
   * Rendered WIDTH in px. Height follows the artwork's own aspect (the
   * drawings run ~2.4:1 and wider), so the element is exactly as tall as what
   * it draws. It used to be a square of this size, which left up to 19px of
   * dead space under an 9px-tall pair of glasses and read as a big gap above
   * whatever came next.
   */
  size?: number;
}

export const IndyScanIndicator: FC<IndyScanIndicatorProps> = ({
  size = 32,
}) => {
  const g = glassesById(useAtomValue(indyGlassesVariantAtom));
  const styleVars = {
    "--indy-scan-travel": `${g.scanTravel}px`,
    width: size,
  } as CSSProperties;

  return (
    <span
      className="indy-scan"
      style={styleVars}
      role="status"
      aria-label="Indy is thinking"
    >
      <svg
        className="indy-scan__glyph"
        viewBox={`0 0 ${g.vbW} 40`}
        fill="none"
        aria-hidden="true"
      >
        <g className="indy-scan__specs">
          <image
            href={g.src}
            x="0"
            y="0"
            width={g.vbW}
            height="40"
            preserveAspectRatio="xMidYMid meet"
          />
          <line
            className="indy-scan__line"
            x1={g.scanX}
            y1={g.scanY1}
            x2={g.scanX}
            y2={g.scanY2}
          />
        </g>
      </svg>
    </span>
  );
};

export default IndyScanIndicator;
